import React from 'react';
import {useTranslation} from 'react-i18next';
import {indirizzoImmagine} from '../../utils/immagini';

/**
 * IL CASSETTO DEI FORNITORI del telefono (sotto i 1024px): nelle pagine di
 * categoria la fascia dei loghi non si vede, per non rubare altezza alla
 * griglia; al suo posto, accanto alla ricerca, c'e' il TASTO «Fornitori»
 * (TastoFornitori) che apre questo cassetto dal basso con TUTTI i fornitori
 * della pagina aperta, coi loro loghi, in griglia. Toccandone uno si filtra
 * la pagina e il cassetto si chiude; quello scelto e' segnato e «Tutti» lo
 * toglie. Con molti fornitori c'e' la casella per cercarne uno.
 *
 * Si chiude con la X, toccando fuori, con Esc, e cambiando pagina (chi lo
 * monta lo chiude al cambio d'indirizzo). Mentre e' aperto la pagina dietro
 * non scorre. Resta montato: aprire e chiudere e' una classe, cosi' sale e
 * scende invece di comparire di colpo; chiuso e' `visibility: hidden` e non
 * si raggiunge col tasto Tab. Dal computer in su non si vede (Casino.css).
 *
 * Nel guscio Nova la barra in basso ha il suo tasto Fornitori, che copre
 * tutte le sezioni: questo e' della pagina aperta, e sta dove sta il filtro.
 *
 * `voci`: [{nome, immagine}] come la fascia. `scelto`: il nome acceso.
 */
export default function CassettoFornitori({aperto, onChiudi, voci, scelto, onScegli, titolo}) {
  const {t} = useTranslation();
  const [cerca, setCerca] = React.useState('');
  const campo = React.useRef(null);

  React.useEffect(() => {
    if (!aperto) return undefined;
    setCerca('');
    const alTasto = (e) => { if (e.key === 'Escape') onChiudi(); };
    document.addEventListener('keydown', alTasto);
    const eraCosi = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', alTasto);
      document.body.style.overflow = eraCosi;
    };
  }, [aperto, onChiudi]);

  const testo = cerca.trim().toLowerCase();
  const mostrati = voci.filter((v) => !testo || (v.nome || '').toLowerCase().includes(testo));
  const scegli = (v) => { onScegli(v); onChiudi(); };

  return (
    <>
      <div className={'cas-cassetto-velo' + (aperto ? ' aperto' : '')} onClick={onChiudi} aria-hidden={'true'}/>
      <section className={'cas-cassetto' + (aperto ? ' aperto' : '')} role={'dialog'} aria-modal={aperto ? 'true' : undefined}
        aria-label={titolo || t('Fornitori')} aria-hidden={aperto ? undefined : 'true'}>
        <span className={'cas-cassetto-maniglia'} aria-hidden={'true'}/>
        <div className={'cas-cassetto-testa'}>
          <h3>{titolo || t('Fornitori')} <small>{voci.length}</small></h3>
          <button type={'button'} className={'cas-cassetto-x'} onClick={onChiudi} aria-label={t('Chiudi')}>✕</button>
        </div>
        {voci.length > 8 && (
          <input ref={campo} type={'search'} className={'cas-cerca cas-cassetto-cerca'} value={cerca} onChange={(e) => setCerca(e.target.value)}
            placeholder={t('Cerca un fornitore')} aria-label={t('Cerca un fornitore')}/>
        )}
        <div className={'cas-cassetto-scorre'}>
          {!!scelto && !testo && (
            <button type={'button'} className={'cas-cassetto-tutti'} onClick={() => scegli({nome: '', immagine: ''})}>
              {t('Tutti i fornitori')}
            </button>
          )}
          {mostrati.length === 0 ? (
            <p className={'cas-cassetto-vuoto'}>{t('Nessun fornitore con questo nome.')}</p>
          ) : (
            <div className={'cas-cassetto-griglia'} role={'list'}>
              {mostrati.map((v, n) => {
                const acceso = !!scelto && v.nome === scelto;
                return (
                  <div role={'listitem'} key={(v.nome || '') + n}>
                    <button type={'button'} className={'cas-cassetto-logo' + (acceso ? ' scelto' : '')} aria-pressed={acceso}
                      onClick={() => scegli(acceso ? {nome: '', immagine: ''} : v)}>
                      <span className={'cas-cassetto-img'}>
                        {v.immagine ? <img src={indirizzoImmagine(v.immagine)} alt={''} loading={'lazy'}/> : <b>{iniziali(v.nome)}</b>}
                      </span>
                      <span className={'cas-cassetto-nome'}>{v.nome}</span>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

/**
 * IL TASTO che apre il cassetto, da mettere fra i filtri della pagina: si
 * vede solo sul telefono (Casino.css). `quanti` e' quanti fornitori ha la
 * pagina; `scelto` il nome acceso, scritto sul tasto cosi' si vede che la
 * pagina e' filtrata anche senza la fascia.
 */
export function TastoFornitori({quanti, scelto, aperto, onApri}) {
  const {t} = useTranslation();
  return (
    <button type={'button'} className={'cas-fornitori-tasto' + (scelto ? ' scelto' : '')} aria-expanded={aperto}
      aria-haspopup={'dialog'} onClick={onApri}>
      <svg viewBox={'0 0 24 24'} width={'16'} height={'16'} fill={'none'} stroke={'currentColor'} strokeWidth={'2'}
        strokeLinecap={'round'} strokeLinejoin={'round'} aria-hidden={'true'}>
        <path d={'m12 3 9 5-9 5-9-5zM3 13l9 5 9-5'}/>
      </svg>
      <span>{scelto || t('Fornitori')}</span>
      {!scelto && quanti > 0 && <small>{quanti}</small>}
    </button>
  );
}

function iniziali(nome) {
  return String(nome || '').replace(/[^\p{L}\p{N} ]/gu, '').split(/\s+/).filter(Boolean).map((w) => w[0]).join('').slice(0, 2).toUpperCase() || '?';
}
